import { useMemo } from "react"

import { BuildingReferenceSearchResult } from "../types/GlobalSearch"
import { useBuildingReferencesByBuildingTypes } from "./useBuildingReferencesByBuildingTypes"
import { useBuildingReferencesByTitle } from "./useBuildingReferencesByTitle"
import { useBuildingReferencesByVariantKey } from "./useBuildingReferencesByVariantKey"

interface UseBuildingReferenceSearchProps {
  searchTerm?: string | null
  variantKeys?: string[] | null
}

export function useBuildingReferenceSearch({
  searchTerm,
  variantKeys
}: UseBuildingReferenceSearchProps) {
  const { results: byTitle, isLoading: isLoadingTitle } =
    useBuildingReferencesByTitle({ searchTerm })
  const { results: byBuildingTypes, isLoading: isLoadingBuildingTypes } =
    useBuildingReferencesByBuildingTypes({ searchTerm })
  const { results: byVariantKey, isLoading: isLoadingVariantKey } =
    useBuildingReferencesByVariantKey({ variantKeys })

  const results = useMemo<BuildingReferenceSearchResult[]>(() => {
    const byId = new Map<string, BuildingReferenceSearchResult>()
    const merged = [...byTitle, ...byBuildingTypes, ...byVariantKey]
    merged.forEach(item => {
      const existing = byId.get(item.id)
      if (existing) {
        byId.set(item.id, {
          ...existing,
          matchInfo: { ...existing.matchInfo, ...item.matchInfo }
        })
      } else {
        byId.set(item.id, item)
      }
    })
    return Array.from(byId.values())
  }, [byTitle, byBuildingTypes, byVariantKey])

  return {
    results,
    isLoading: isLoadingTitle || isLoadingBuildingTypes || isLoadingVariantKey
  }
}
